/**
 * Search areas used to pick random locations.
 *
 * These are real cities and towns, used ONLY as starting points: the provider then
 * searches for a real street-level panorama in a random area around them (several
 * kilometres to tens of kilometres away). The location the player has to guess is
 * always the GPS position returned by the imagery provider, never these coordinates.
 */

export type Continent = 'europe' | 'asia' | 'oceania' | 'north-america' | 'south-america' | 'africa';

export type ProviderWeights = {
  /** Relative weight for Google Street View (0 = no official coverage). */
  google: number;
  /** Relative weight for Mapillary (0 = excluded). */
  mapillary: number;
  /** Relative weight for Panoramax (0 = no known 360° coverage). */
  panoramax: number;
};

export interface Country {
  code: string;
  name: string;
  continent: Continent;
  weights: ProviderWeights;
}

/** [name, lat, lng, continent override?] */
export type PlaceTuple = [string, number, number, Continent?];

export interface Place {
  name: string;
  country: string;
  lat: number;
  lng: number;
  continent: Continent;
}

const w = (google: number, mapillary = google, panoramax = 0): ProviderWeights => ({ google, mapillary, panoramax });

export const COUNTRIES: Country[] = [
  // Europe
  { code: 'FR', name: 'France', continent: 'europe', weights: w(5, 5, 60) },
  { code: 'DE', name: 'Allemagne', continent: 'europe', weights: w(4, 5, 1.5) },
  { code: 'ES', name: 'Espagne', continent: 'europe', weights: w(4, 4, 1) },
  { code: 'IT', name: 'Italie', continent: 'europe', weights: w(4, 4, 1) },
  { code: 'GB', name: 'Royaume-Uni', continent: 'europe', weights: w(4, 4, 0.5) },
  { code: 'IE', name: 'Irlande', continent: 'europe', weights: w(1, 1) },
  { code: 'PT', name: 'Portugal', continent: 'europe', weights: w(2, 2, 0.5) },
  { code: 'BE', name: 'Belgique', continent: 'europe', weights: w(1, 1, 1.5) },
  { code: 'NL', name: 'Pays-Bas', continent: 'europe', weights: w(1, 1.5, 0.5) },
  { code: 'LU', name: 'Luxembourg', continent: 'europe', weights: w(0.3, 0.3, 0.5) },
  { code: 'CH', name: 'Suisse', continent: 'europe', weights: w(1, 1, 1) },
  { code: 'AT', name: 'Autriche', continent: 'europe', weights: w(1, 1, 0.3) },
  { code: 'DK', name: 'Danemark', continent: 'europe', weights: w(1, 1) },
  { code: 'NO', name: 'Norvège', continent: 'europe', weights: w(2, 1.5) },
  { code: 'SE', name: 'Suède', continent: 'europe', weights: w(2, 1.5) },
  { code: 'FI', name: 'Finlande', continent: 'europe', weights: w(2, 1.5) },
  { code: 'IS', name: 'Islande', continent: 'europe', weights: w(0.5, 0.5) },
  { code: 'PL', name: 'Pologne', continent: 'europe', weights: w(3, 3, 0.2) },
  { code: 'CZ', name: 'Tchéquie', continent: 'europe', weights: w(1, 1) },
  { code: 'SK', name: 'Slovaquie', continent: 'europe', weights: w(0.7, 0.7) },
  { code: 'HU', name: 'Hongrie', continent: 'europe', weights: w(1, 1) },
  { code: 'SI', name: 'Slovénie', continent: 'europe', weights: w(0.4, 0.4) },
  { code: 'HR', name: 'Croatie', continent: 'europe', weights: w(1, 1) },
  { code: 'RS', name: 'Serbie', continent: 'europe', weights: w(1, 1) },
  { code: 'ME', name: 'Monténégro', continent: 'europe', weights: w(0.3, 0.3) },
  { code: 'AL', name: 'Albanie', continent: 'europe', weights: w(0.5, 0.5) },
  { code: 'MK', name: 'Macédoine du Nord', continent: 'europe', weights: w(0.4, 0.4) },
  { code: 'BG', name: 'Bulgarie', continent: 'europe', weights: w(1, 1) },
  { code: 'RO', name: 'Roumanie', continent: 'europe', weights: w(2, 2) },
  { code: 'GR', name: 'Grèce', continent: 'europe', weights: w(1.5, 1.5) },
  { code: 'EE', name: 'Estonie', continent: 'europe', weights: w(0.4, 0.4) },
  { code: 'LV', name: 'Lettonie', continent: 'europe', weights: w(0.4, 0.4) },
  { code: 'LT', name: 'Lituanie', continent: 'europe', weights: w(0.5, 0.5) },
  { code: 'UA', name: 'Ukraine', continent: 'europe', weights: w(2, 2) },
  { code: 'RU', name: 'Russie', continent: 'europe', weights: w(6, 4) },
  { code: 'MT', name: 'Malte', continent: 'europe', weights: w(0.2, 0.2) },
  { code: 'AD', name: 'Andorre', continent: 'europe', weights: w(0.1, 0.1) },
  // Asia
  { code: 'TR', name: 'Turquie', continent: 'asia', weights: w(3, 3) },
  { code: 'JP', name: 'Japon', continent: 'asia', weights: w(5, 4) },
  { code: 'KR', name: 'Corée du Sud', continent: 'asia', weights: w(2, 1) },
  { code: 'TW', name: 'Taïwan', continent: 'asia', weights: w(1, 1) },
  { code: 'HK', name: 'Hong Kong', continent: 'asia', weights: w(0.3, 0.3) },
  { code: 'TH', name: 'Thaïlande', continent: 'asia', weights: w(3, 2) },
  { code: 'MY', name: 'Malaisie', continent: 'asia', weights: w(2, 1.5) },
  { code: 'SG', name: 'Singapour', continent: 'asia', weights: w(0.3, 0.3) },
  { code: 'ID', name: 'Indonésie', continent: 'asia', weights: w(4, 3) },
  { code: 'PH', name: 'Philippines', continent: 'asia', weights: w(3, 2) },
  { code: 'KH', name: 'Cambodge', continent: 'asia', weights: w(1, 0.5) },
  { code: 'LA', name: 'Laos', continent: 'asia', weights: w(0.5, 0.3) },
  { code: 'VN', name: 'Viêt Nam', continent: 'asia', weights: w(0.5, 1) },
  { code: 'IN', name: 'Inde', continent: 'asia', weights: w(4, 3) },
  { code: 'LK', name: 'Sri Lanka', continent: 'asia', weights: w(1, 0.5) },
  { code: 'BD', name: 'Bangladesh', continent: 'asia', weights: w(1, 0.5) },
  { code: 'BT', name: 'Bhoutan', continent: 'asia', weights: w(0.3, 0.1) },
  { code: 'NP', name: 'Népal', continent: 'asia', weights: w(0.3, 0.5) },
  { code: 'MN', name: 'Mongolie', continent: 'asia', weights: w(1, 0.5) },
  { code: 'KZ', name: 'Kazakhstan', continent: 'asia', weights: w(2, 1) },
  { code: 'KG', name: 'Kirghizistan', continent: 'asia', weights: w(0.6, 0.4) },
  { code: 'IL', name: 'Israël', continent: 'asia', weights: w(1, 1) },
  { code: 'JO', name: 'Jordanie', continent: 'asia', weights: w(0.6, 0.4) },
  { code: 'AE', name: 'Émirats arabes unis', continent: 'asia', weights: w(0.5, 0.5) },
  { code: 'QA', name: 'Qatar', continent: 'asia', weights: w(0.3, 0.2) },
  { code: 'LB', name: 'Liban', continent: 'asia', weights: w(0.3, 0.2) },
  // Oceania
  { code: 'AU', name: 'Australie', continent: 'oceania', weights: w(5, 4) },
  { code: 'NZ', name: 'Nouvelle-Zélande', continent: 'oceania', weights: w(2, 1.5) },
  // North & Central America, Caribbean
  { code: 'US', name: 'États-Unis', continent: 'north-america', weights: w(10, 10) },
  { code: 'CA', name: 'Canada', continent: 'north-america', weights: w(5, 4) },
  { code: 'MX', name: 'Mexique', continent: 'north-america', weights: w(4, 3) },
  { code: 'GT', name: 'Guatemala', continent: 'north-america', weights: w(0.5, 0.4) },
  { code: 'CR', name: 'Costa Rica', continent: 'north-america', weights: w(0.5, 0.4) },
  { code: 'PA', name: 'Panama', continent: 'north-america', weights: w(0.3, 0.3) },
  { code: 'DO', name: 'République dominicaine', continent: 'north-america', weights: w(0.5, 0.3) },
  { code: 'PR', name: 'Porto Rico', continent: 'north-america', weights: w(0.3, 0.3) },
  // South America
  { code: 'BR', name: 'Brésil', continent: 'south-america', weights: w(6, 5) },
  { code: 'AR', name: 'Argentine', continent: 'south-america', weights: w(3, 2) },
  { code: 'CL', name: 'Chili', continent: 'south-america', weights: w(2, 1.5) },
  { code: 'PE', name: 'Pérou', continent: 'south-america', weights: w(2, 1) },
  { code: 'CO', name: 'Colombie', continent: 'south-america', weights: w(2, 1.5) },
  { code: 'EC', name: 'Équateur', continent: 'south-america', weights: w(1, 0.7) },
  { code: 'BO', name: 'Bolivie', continent: 'south-america', weights: w(1, 0.5) },
  { code: 'UY', name: 'Uruguay', continent: 'south-america', weights: w(0.6, 0.5) },
  { code: 'PY', name: 'Paraguay', continent: 'south-america', weights: w(0.5, 0.4) },
  // Africa
  { code: 'ZA', name: 'Afrique du Sud', continent: 'africa', weights: w(3, 2) },
  { code: 'BW', name: 'Botswana', continent: 'africa', weights: w(0.6, 0.3) },
  { code: 'LS', name: 'Lesotho', continent: 'africa', weights: w(0.2, 0.1) },
  { code: 'SZ', name: 'Eswatini', continent: 'africa', weights: w(0.2, 0.1) },
  { code: 'KE', name: 'Kenya', continent: 'africa', weights: w(1, 1) },
  { code: 'UG', name: 'Ouganda', continent: 'africa', weights: w(0.6, 0.5) },
  { code: 'RW', name: 'Rwanda', continent: 'africa', weights: w(0.2, 0.2) },
  { code: 'NG', name: 'Nigeria', continent: 'africa', weights: w(0.8, 0.6) },
  { code: 'GH', name: 'Ghana', continent: 'africa', weights: w(0.8, 0.5) },
  { code: 'SN', name: 'Sénégal', continent: 'africa', weights: w(0.6, 0.5) },
  { code: 'TN', name: 'Tunisie', continent: 'africa', weights: w(0.5, 0.4) },
  { code: 'MG', name: 'Madagascar', continent: 'africa', weights: w(0.3, 0.3) },
  // Countries with community imagery (Mapillary) but no official Street View coverage
  { code: 'MA', name: 'Maroc', continent: 'africa', weights: w(0, 0.8) },
  { code: 'EG', name: 'Égypte', continent: 'africa', weights: w(0, 0.5) },
  { code: 'TZ', name: 'Tanzanie', continent: 'africa', weights: w(0, 0.5) },
  { code: 'NA', name: 'Namibie', continent: 'africa', weights: w(0, 0.4) },
];

export const COUNTRY_BY_CODE: Record<string, Country> = Object.fromEntries(COUNTRIES.map((c) => [c.code, c]));

const PLACE_TUPLES: Record<string, PlaceTuple[]> = {
  FR: [
    ['Paris', 48.8566, 2.3522], ['Lyon', 45.764, 4.8357], ['Marseille', 43.2965, 5.3698],
    ['Toulouse', 43.6047, 1.4442], ['Nice', 43.7102, 7.262], ['Nantes', 47.2184, -1.5536],
    ['Strasbourg', 48.5734, 7.7521], ['Montpellier', 43.6108, 3.8767], ['Bordeaux', 44.8378, -0.5792],
    ['Lille', 50.6292, 3.0573], ['Rennes', 48.1173, -1.6778], ['Reims', 49.2583, 4.0317],
    ['Le Havre', 49.4944, 0.1079], ['Saint-Étienne', 45.4397, 4.3872], ['Toulon', 43.1242, 5.928],
    ['Grenoble', 45.1885, 5.7245], ['Dijon', 47.322, 5.0415], ['Angers', 47.4784, -0.5632],
    ['Nîmes', 43.8367, 4.3601], ['Clermont-Ferrand', 45.7772, 3.087], ['Le Mans', 48.0061, 0.1996],
    ['Aix-en-Provence', 43.5297, 5.4474], ['Brest', 48.3904, -4.4861], ['Tours', 47.3941, 0.6848],
    ['Amiens', 49.8941, 2.2958], ['Limoges', 45.8336, 1.2611], ['Annecy', 45.8992, 6.1294],
    ['Perpignan', 42.6887, 2.8948], ['Metz', 49.1193, 6.1757], ['Besançon', 47.2378, 6.0241],
    ['Orléans', 47.903, 1.9093], ['Rouen', 49.4432, 1.0999], ['Caen', 49.1829, -0.3707],
    ['Nancy', 48.6921, 6.1844], ['Mulhouse', 47.7508, 7.3359], ['Poitiers', 46.5802, 0.3404],
    ['Pau', 43.2951, -0.3708], ['La Rochelle', 46.1603, -1.1511], ['Bayonne', 43.4929, -1.4748],
    ['Avignon', 43.9493, 4.8055], ['Ajaccio', 41.9192, 8.7386], ['Bastia', 42.6977, 9.4508],
    ['Quimper', 47.996, -4.1024], ['Vannes', 47.6582, -2.7608], ['Lorient', 47.7483, -3.37],
    ['Saint-Brieuc', 48.5136, -2.7653], ['Chambéry', 45.5646, 5.9178], ['Valence', 44.9334, 4.8924],
    ['Troyes', 48.2973, 4.0744], ['Chartres', 48.4439, 1.489], ['Bourges', 47.081, 2.3988],
    ['Niort', 46.3237, -0.4588], ['Angoulême', 45.6484, 0.1562], ['Périgueux', 45.1846, 0.7214],
    ['Agen', 44.2033, 0.6163], ['Albi', 43.9289, 2.1464], ['Rodez', 44.3506, 2.575],
    ['Montauban', 44.0176, 1.355], ['Tarbes', 43.2328, 0.0781], ['Carcassonne', 43.213, 2.3491],
    ['Béziers', 43.3442, 3.2158], ['Gap', 44.5594, 6.0786], ['Digne-les-Bains', 44.0925, 6.2356],
    ['Auxerre', 47.7982, 3.5673], ['Nevers', 46.9896, 3.159], ['Moulins', 46.566, 3.3326],
    ['Aurillac', 44.9264, 2.4396], ['Le Puy-en-Velay', 45.0434, 3.8858], ['Mende', 44.5181, 3.5005],
    ['Épinal', 48.1724, 6.4496], ['Colmar', 48.0794, 7.3585], ['Calais', 50.9513, 1.8587],
    ['Dunkerque', 51.0343, 2.3768], ['Arras', 50.291, 2.7775], ['Beauvais', 49.4295, 2.0807],
    ['Saint-Malo', 48.6493, -2.0257], ['Cherbourg', 49.6337, -1.6222], ['Laval', 48.0707, -0.7734],
    ['La Roche-sur-Yon', 46.6705, -1.426], ['Blois', 47.5861, 1.3359], ['Châteauroux', 46.8103, 1.6913],
    ['Guéret', 46.1716, 1.8717], ['Tulle', 45.2671, 1.771], ['Cahors', 44.4475, 1.4419],
    ['Foix', 42.9653, 1.6075], ['Mont-de-Marsan', 43.8902, -0.4995], ['Évreux', 49.027, 1.1508],
    ['Alençon', 48.4329, 0.0913], ['Charleville-Mézières', 49.7621, 4.7262], ['Bar-le-Duc', 48.7727, 5.16],
    ['Chaumont', 48.111, 5.139], ['Vesoul', 47.6197, 6.1545], ['Lons-le-Saunier', 46.6744, 5.5547],
    ['Mâcon', 46.3069, 4.8287], ['Bourg-en-Bresse', 46.2052, 5.2255], ['Privas', 44.7353, 4.5986],
    ['Saint-Lô', 49.1157, -1.0906], ['Versailles', 48.8049, 2.1204], ['Melun', 48.5393, 2.6608],
    ['Cergy', 49.0364, 2.0761], ['Évry', 48.6239, 2.429], ['Créteil', 48.7904, 2.4556],
  ],
  DE: [
    ['Berlin', 52.52, 13.405], ['Hambourg', 53.5511, 9.9937], ['Munich', 48.1351, 11.582],
    ['Cologne', 50.9375, 6.9603], ['Francfort', 50.1109, 8.6821], ['Stuttgart', 48.7758, 9.1829],
    ['Düsseldorf', 51.2277, 6.7735], ['Leipzig', 51.3397, 12.3731], ['Dresde', 51.0504, 13.7373],
    ['Hanovre', 52.3759, 9.732], ['Nuremberg', 49.4521, 11.0767], ['Brême', 53.0793, 8.8017],
    ['Fribourg-en-Brisgau', 47.999, 7.8421], ['Rostock', 54.0924, 12.0991], ['Cassel', 51.3127, 9.4797],
    ['Erfurt', 50.9848, 11.0299], ['Ratisbonne', 49.0134, 12.1016], ['Kiel', 54.3233, 10.1228],
    ['Münster', 51.9607, 7.6261], ['Sarrebruck', 49.2402, 6.9969],
  ],
  ES: [
    ['Madrid', 40.4168, -3.7038], ['Barcelone', 41.3874, 2.1686], ['Valence', 39.4699, -0.3763],
    ['Séville', 37.3891, -5.9845], ['Saragosse', 41.6488, -0.8891], ['Malaga', 36.7213, -4.4214],
    ['Bilbao', 43.263, -2.935], ['Valladolid', 41.6523, -4.7245], ['La Corogne', 43.3623, -8.4115],
    ['Grenade', 37.1773, -3.5986], ['Murcie', 37.9922, -1.1307], ['Oviedo', 43.3614, -5.8593],
    ['Salamanque', 40.9701, -5.6635], ['Badajoz', 38.8794, -6.9707], ['Albacete', 38.9943, -1.8585],
    ['Palma', 39.5696, 2.6502], ['Pampelune', 42.8125, -1.6458], ['Cáceres', 39.4753, -6.3724],
    ['León', 42.5987, -5.5671], ['Almería', 36.834, -2.4637],
  ],
  IT: [
    ['Rome', 41.9028, 12.4964], ['Milan', 45.4642, 9.19], ['Naples', 40.8518, 14.2681],
    ['Turin', 45.0703, 7.6869], ['Palerme', 38.1157, 13.3615], ['Gênes', 44.4056, 8.9463],
    ['Bologne', 44.4949, 11.3426], ['Florence', 43.7696, 11.2558], ['Bari', 41.1171, 16.8719],
    ['Catane', 37.5079, 15.083], ['Venise', 45.4408, 12.3155], ['Vérone', 45.4384, 10.9916],
    ['Cagliari', 39.2238, 9.1217], ['Pérouse', 43.1107, 12.3908], ['Pescara', 42.4618, 14.216],
    ['Reggio de Calabre', 38.1113, 15.6473], ['Trente', 46.0748, 11.1217], ['Lecce', 40.3515, 18.175],
    ['Ancône', 43.6158, 13.5189], ['Potenza', 40.6404, 15.8056], ['Sassari', 40.7259, 8.5557],
  ],
  GB: [
    ['Londres', 51.5074, -0.1278], ['Birmingham', 52.4862, -1.8904], ['Manchester', 53.4808, -2.2426],
    ['Leeds', 53.8008, -1.5491], ['Glasgow', 55.8642, -4.2518], ['Édimbourg', 55.9533, -3.1883],
    ['Liverpool', 53.4084, -2.9916], ['Bristol', 51.4545, -2.5879], ['Cardiff', 51.4816, -3.1791],
    ['Belfast', 54.5973, -5.9301], ['Newcastle', 54.9783, -1.6178], ['Sheffield', 53.3811, -1.4701],
    ['Nottingham', 52.9548, -1.1581], ['Aberdeen', 57.1497, -2.0943], ['Inverness', 57.4778, -4.2247],
    ['Plymouth', 50.3755, -4.1427], ['Norwich', 52.6309, 1.2974], ['Exeter', 50.7184, -3.5339],
    ['York', 53.96, -1.0873], ['Swansea', 51.6214, -3.9436], ['Carlisle', 54.8925, -2.9329],
    ['Oxford', 51.752, -1.2577],
  ],
  IE: [
    ['Dublin', 53.3498, -6.2603], ['Cork', 51.8985, -8.4756], ['Galway', 53.2707, -9.0568],
    ['Limerick', 52.6638, -8.6267], ['Sligo', 54.2766, -8.4761], ['Waterford', 52.2593, -7.1101],
    ['Letterkenny', 54.9558, -7.7342],
  ],
  PT: [
    ['Lisbonne', 38.7223, -9.1393], ['Porto', 41.1579, -8.6291], ['Braga', 41.5454, -8.4265],
    ['Coimbra', 40.2033, -8.4103], ['Faro', 37.0194, -7.9322], ['Évora', 38.5714, -7.9135],
    ['Viseu', 40.6566, -7.9125], ['Bragança', 41.8061, -6.7567], ['Castelo Branco', 39.8222, -7.4909],
  ],
  BE: [
    ['Bruxelles', 50.8503, 4.3517], ['Anvers', 51.2194, 4.4025], ['Gand', 51.0543, 3.7174],
    ['Liège', 50.6326, 5.5797], ['Namur', 50.4674, 4.872], ['Bruges', 51.2093, 3.2247],
    ['Arlon', 49.6833, 5.8167],
  ],
  NL: [
    ['Amsterdam', 52.3676, 4.9041], ['Rotterdam', 51.9244, 4.4777], ['Utrecht', 52.0907, 5.1214],
    ['Groningue', 53.2194, 6.5665], ['Eindhoven', 51.4416, 5.4697], ['Maastricht', 50.8514, 5.691],
    ['Zwolle', 52.5168, 6.083],
  ],
  LU: [['Luxembourg', 49.6116, 6.1319]],
  CH: [
    ['Zurich', 47.3769, 8.5417], ['Genève', 46.2044, 6.1432], ['Berne', 46.948, 7.4474],
    ['Lausanne', 46.5197, 6.6323], ['Bâle', 47.5596, 7.5886], ['Lugano', 46.0037, 8.9511],
    ['Coire', 46.8508, 9.532], ['Sion', 46.2331, 7.3606], ['Lucerne', 47.0502, 8.3093],
  ],
  AT: [
    ['Vienne', 48.2082, 16.3738], ['Graz', 47.0707, 15.4395], ['Linz', 48.3069, 14.2858],
    ['Salzbourg', 47.8095, 13.055], ['Innsbruck', 47.2692, 11.4041], ['Klagenfurt', 46.6249, 14.305],
  ],
  DK: [
    ['Copenhague', 55.6761, 12.5683], ['Aarhus', 56.1629, 10.2039], ['Odense', 55.4038, 10.4024],
    ['Aalborg', 57.0488, 9.9217], ['Esbjerg', 55.4765, 8.4594],
  ],
  NO: [
    ['Oslo', 59.9139, 10.7522], ['Bergen', 60.3913, 5.3221], ['Trondheim', 63.4305, 10.3951],
    ['Stavanger', 58.97, 5.7331], ['Tromsø', 69.6492, 18.9553], ['Bodø', 67.2804, 14.4049],
    ['Kristiansand', 58.1599, 8.0182], ['Ålesund', 62.4722, 6.1495], ['Lillehammer', 61.1153, 10.4662],
  ],
  SE: [
    ['Stockholm', 59.3293, 18.0686], ['Göteborg', 57.7089, 11.9746], ['Malmö', 55.605, 13.0038],
    ['Uppsala', 59.8586, 17.6389], ['Umeå', 63.8258, 20.263], ['Luleå', 65.5848, 22.1547],
    ['Östersund', 63.1792, 14.6357], ['Sundsvall', 62.3908, 17.3069], ['Karlstad', 59.4022, 13.5115],
    ['Kiruna', 67.8558, 20.2253], ['Jönköping', 57.7826, 14.1618],
  ],
  FI: [
    ['Helsinki', 60.1699, 24.9384], ['Tampere', 61.4978, 23.761], ['Turku', 60.4518, 22.2666],
    ['Oulu', 65.0121, 25.4651], ['Rovaniemi', 66.5039, 25.7294], ['Kuopio', 62.8924, 27.677],
    ['Jyväskylä', 62.2426, 25.7473], ['Vaasa', 63.0951, 21.6165], ['Joensuu', 62.601, 29.7636],
  ],
  IS: [
    ['Reykjavik', 64.1466, -21.9426], ['Akureyri', 65.6885, -18.1262], ['Egilsstaðir', 65.2669, -14.3948],
    ['Selfoss', 63.9331, -20.9971],
  ],
  PL: [
    ['Varsovie', 52.2297, 21.0122], ['Cracovie', 50.0647, 19.945], ['Łódź', 51.7592, 19.456],
    ['Wrocław', 51.1079, 17.0385], ['Poznań', 52.4064, 16.9252], ['Gdańsk', 54.352, 18.6466],
    ['Szczecin', 53.4285, 14.5528], ['Lublin', 51.2465, 22.5684], ['Białystok', 53.1325, 23.1688],
    ['Rzeszów', 50.0412, 21.9991], ['Olsztyn', 53.7784, 20.4801], ['Katowice', 50.2649, 19.0238],
  ],
  CZ: [
    ['Prague', 50.0755, 14.4378], ['Brno', 49.1951, 16.6068], ['Ostrava', 49.8209, 18.2625],
    ['Plzeň', 49.7384, 13.3736], ['České Budějovice', 48.9745, 14.4743], ['Liberec', 50.7663, 15.0543],
  ],
  SK: [
    ['Bratislava', 48.1486, 17.1077], ['Košice', 48.7164, 21.2611], ['Žilina', 49.2231, 18.7394],
    ['Banská Bystrica', 48.7395, 19.1535],
  ],
  HU: [
    ['Budapest', 47.4979, 19.0402], ['Debrecen', 47.5316, 21.6273], ['Szeged', 46.253, 20.1414],
    ['Pécs', 46.0727, 18.2323], ['Győr', 47.6875, 17.6504], ['Miskolc', 48.1035, 20.7784],
  ],
  SI: [['Ljubljana', 46.0569, 14.5058], ['Maribor', 46.5547, 15.6459], ['Koper', 45.5481, 13.7302]],
  HR: [
    ['Zagreb', 45.815, 15.9819], ['Split', 43.5081, 16.4402], ['Rijeka', 45.3271, 14.4422],
    ['Osijek', 45.555, 18.6955], ['Zadar', 44.1194, 15.2314], ['Dubrovnik', 42.6507, 18.0944],
  ],
  RS: [
    ['Belgrade', 44.7866, 20.4489], ['Novi Sad', 45.2671, 19.8335], ['Niš', 43.3209, 21.8958],
    ['Kragujevac', 44.0128, 20.9114],
  ],
  ME: [['Podgorica', 42.4304, 19.2594], ['Kotor', 42.4247, 18.7712]],
  AL: [['Tirana', 41.3275, 19.8187], ['Shkodër', 42.0693, 19.5033], ['Vlorë', 40.466, 19.4914]],
  MK: [['Skopje', 41.9981, 21.4254], ['Bitola', 41.0297, 21.3292]],
  BG: [
    ['Sofia', 42.6977, 23.3219], ['Plovdiv', 42.1354, 24.7453], ['Varna', 43.2141, 27.9147],
    ['Bourgas', 42.5048, 27.4626], ['Roussé', 43.8356, 25.9657], ['Veliko Tarnovo', 43.0757, 25.6172],
  ],
  RO: [
    ['Bucarest', 44.4268, 26.1025], ['Cluj-Napoca', 46.7712, 23.6236], ['Timișoara', 45.7489, 21.2087],
    ['Iași', 47.1585, 27.6014], ['Constanța', 44.1598, 28.6348], ['Brașov', 45.6427, 25.5887],
    ['Craiova', 44.3302, 23.7949], ['Sibiu', 45.7983, 24.1256], ['Suceava', 47.6514, 26.2556],
    ['Oradea', 47.0465, 21.9189],
  ],
  GR: [
    ['Athènes', 37.9838, 23.7275], ['Thessalonique', 40.6401, 22.9444], ['Patras', 38.2466, 21.7346],
    ['Héraklion', 35.3387, 25.1442], ['Ioannina', 39.665, 20.8537], ['Larissa', 39.639, 22.4191],
    ['Kalamata', 37.0389, 22.1142], ['Alexandroupoli', 40.8457, 25.8739],
  ],
  EE: [['Tallinn', 59.437, 24.7536], ['Tartu', 58.378, 26.729], ['Pärnu', 58.3859, 24.4971]],
  LV: [['Riga', 56.9496, 24.1052], ['Daugavpils', 55.8749, 26.5362], ['Liepāja', 56.5047, 21.0108]],
  LT: [
    ['Vilnius', 54.6872, 25.2797], ['Kaunas', 54.8985, 23.9036], ['Klaipėda', 55.7033, 21.1443],
    ['Šiauliai', 55.9349, 23.3137],
  ],
  UA: [
    ['Kyiv', 50.4501, 30.5234], ['Lviv', 49.8397, 24.0297], ['Odesa', 46.4825, 30.7233],
    ['Kharkiv', 49.9935, 36.2304], ['Dnipro', 48.4647, 35.0462], ['Vinnytsia', 49.2331, 28.4682],
    ['Oujhorod', 48.6208, 22.2879], ['Poltava', 49.5883, 34.5514],
  ],
  RU: [
    ['Moscou', 55.7558, 37.6173], ['Saint-Pétersbourg', 59.9311, 30.3609], ['Kazan', 55.7963, 49.1088],
    ['Nijni Novgorod', 56.2965, 43.9361], ['Samara', 53.1959, 50.1002], ['Volgograd', 48.708, 44.5133],
    ['Rostov-sur-le-Don', 47.2357, 39.7015], ['Voronej', 51.672, 39.1843], ['Perm', 58.0105, 56.2502],
    ['Arkhangelsk', 64.5399, 40.5152], ['Mourmansk', 68.9585, 33.0827], ['Krasnodar', 45.0355, 38.9753],
    ['Oufa', 54.7388, 55.9721],
    ['Iekaterinbourg', 56.8389, 60.6057, 'asia'], ['Novossibirsk', 55.0084, 82.9357, 'asia'],
    ['Omsk', 54.9885, 73.3242, 'asia'], ['Krasnoïarsk', 56.0153, 92.8932, 'asia'],
    ['Irkoutsk', 52.287, 104.305, 'asia'], ['Vladivostok', 43.1155, 131.8855, 'asia'],
    ['Khabarovsk', 48.4802, 135.0719, 'asia'], ['Tioumen', 57.153, 65.5343, 'asia'],
    ['Tcheliabinsk', 55.1644, 61.4368, 'asia'], ['Barnaoul', 53.3561, 83.7496, 'asia'],
    ['Tomsk', 56.4847, 84.9482, 'asia'], ['Iakoutsk', 62.0355, 129.6755, 'asia'],
    ['Oulan-Oude', 51.8335, 107.5841, 'asia'],
  ],
  MT: [['La Valette', 35.8989, 14.5146]],
  AD: [['Andorre-la-Vieille', 42.5063, 1.5218]],
  TR: [
    ['Istanbul', 41.0082, 28.9784, 'europe'], ['Edirne', 41.6771, 26.5557, 'europe'],
    ['Ankara', 39.9334, 32.8597], ['Izmir', 38.4237, 27.1428], ['Antalya', 36.8969, 30.7133],
    ['Bursa', 40.1885, 29.061], ['Adana', 37.0, 35.3213], ['Konya', 37.8746, 32.4932],
    ['Trabzon', 41.0027, 39.7168], ['Erzurum', 39.9043, 41.2679], ['Diyarbakır', 37.9144, 40.2306],
    ['Gaziantep', 37.0662, 37.3833], ['Samsun', 41.2867, 36.33], ['Van', 38.5012, 43.373],
  ],
  JP: [
    ['Tokyo', 35.6762, 139.6503], ['Osaka', 34.6937, 135.5023], ['Nagoya', 35.1815, 136.9066],
    ['Sapporo', 43.0618, 141.3545], ['Fukuoka', 33.5904, 130.4017], ['Sendai', 38.2682, 140.8694],
    ['Hiroshima', 34.3853, 132.4553], ['Kyoto', 35.0116, 135.7681], ['Niigata', 37.9161, 139.0364],
    ['Kagoshima', 31.5966, 130.5571], ['Kanazawa', 36.5613, 136.6562], ['Matsuyama', 33.8392, 132.7657],
    ['Aomori', 40.8244, 140.74], ['Kōchi', 33.5597, 133.5311], ['Asahikawa', 43.7706, 142.365],
    ['Naha', 26.2124, 127.6809], ['Matsumoto', 36.2381, 137.972], ['Akita', 39.72, 140.1025],
    ['Kushiro', 42.9849, 144.382], ['Tottori', 35.5011, 134.2351],
  ],
  KR: [
    ['Séoul', 37.5665, 126.978], ['Busan', 35.1796, 129.0756], ['Daegu', 35.8714, 128.6014],
    ['Gwangju', 35.1595, 126.8526], ['Daejeon', 36.3504, 127.3845], ['Gangneung', 37.7519, 128.8761],
    ['Jeju', 33.4996, 126.5312], ['Andong', 36.5684, 128.7294],
  ],
  TW: [
    ['Taipei', 25.033, 121.5654], ['Taichung', 24.1477, 120.6736], ['Kaohsiung', 22.6273, 120.3014],
    ['Hualien', 23.9872, 121.6015], ['Tainan', 22.9999, 120.227], ['Taitung', 22.7583, 121.1444],
  ],
  HK: [['Hong Kong', 22.3193, 114.1694]],
  TH: [
    ['Bangkok', 13.7563, 100.5018], ['Chiang Mai', 18.7883, 98.9853], ['Phuket', 7.8804, 98.3923],
    ['Khon Kaen', 16.4419, 102.836], ['Udon Thani', 17.4138, 102.787], ['Nakhon Ratchasima', 14.9799, 102.0978],
    ['Hat Yai', 7.0086, 100.4747], ['Surat Thani', 9.1382, 99.3215], ['Chiang Rai', 19.9105, 99.8406],
    ['Phitsanulok', 16.8211, 100.2659], ['Ubon Ratchathani', 15.2287, 104.8564],
  ],
  MY: [
    ['Kuala Lumpur', 3.139, 101.6869], ['George Town', 5.4141, 100.3288], ['Johor Bahru', 1.4927, 103.7414],
    ['Kota Kinabalu', 5.9804, 116.0735], ['Kuching', 1.5535, 110.3593], ['Ipoh', 4.5975, 101.0901],
    ['Kuantan', 3.8077, 103.326], ['Kota Bharu', 6.1254, 102.2381], ['Miri', 4.3995, 113.9914],
  ],
  SG: [['Singapour', 1.3521, 103.8198]],
  ID: [
    ['Jakarta', -6.2088, 106.8456], ['Surabaya', -7.2575, 112.7521], ['Bandung', -6.9175, 107.6191],
    ['Medan', 3.5952, 98.6722], ['Yogyakarta', -7.7956, 110.3695], ['Denpasar', -8.6705, 115.2126],
    ['Makassar', -5.1477, 119.4327], ['Semarang', -6.9667, 110.4167], ['Palembang', -2.9761, 104.7754],
    ['Padang', -0.9471, 100.4172], ['Balikpapan', -1.2379, 116.8529], ['Manado', 1.4748, 124.8421],
    ['Malang', -7.9666, 112.6326], ['Pekanbaru', 0.5071, 101.4478], ['Banda Aceh', 5.5483, 95.3238],
    ['Mataram', -8.5833, 116.1167], ['Kupang', -10.1772, 123.607], ['Pontianak', -0.0263, 109.3425],
  ],
  PH: [
    ['Manille', 14.5995, 120.9842], ['Cebu', 10.3157, 123.8854], ['Davao', 7.1907, 125.4553],
    ['Baguio', 16.4023, 120.596], ['Iloilo', 10.7202, 122.5621], ['Cagayan de Oro', 8.4542, 124.6319],
    ['Legazpi', 13.1391, 123.7438], ['Puerto Princesa', 9.7392, 118.7353], ['Laoag', 18.196, 120.5927],
    ['Tacloban', 11.2443, 125.004], ['Zamboanga', 6.9214, 122.079], ['Tuguegarao', 17.6132, 121.727],
  ],
  KH: [
    ['Phnom Penh', 11.5564, 104.9282], ['Siem Reap', 13.3671, 103.8448], ['Battambang', 13.0957, 103.2022],
    ['Kampot', 10.6104, 104.1815],
  ],
  LA: [['Vientiane', 17.9757, 102.6331], ['Luang Prabang', 19.8856, 102.1347], ['Paksé', 15.1202, 105.799]],
  VN: [
    ['Hanoï', 21.0278, 105.8342], ['Hô Chi Minh-Ville', 10.8231, 106.6297], ['Da Nang', 16.0544, 108.2022],
    ['Hué', 16.4637, 107.5909], ['Cần Thơ', 10.0452, 105.7469], ['Nha Trang', 12.2388, 109.1967],
  ],
  IN: [
    ['Delhi', 28.6139, 77.209], ['Mumbai', 19.076, 72.8777], ['Bangalore', 12.9716, 77.5946],
    ['Chennai', 13.0827, 80.2707], ['Kolkata', 22.5726, 88.3639], ['Hyderabad', 17.385, 78.4867],
    ['Pune', 18.5204, 73.8567], ['Ahmedabad', 23.0225, 72.5714], ['Jaipur', 26.9124, 75.7873],
    ['Lucknow', 26.8467, 80.9462], ['Kochi', 9.9312, 76.2673], ['Bhopal', 23.2599, 77.4126],
    ['Nagpur', 21.1458, 79.0882], ['Indore', 22.7196, 75.8577], ['Coimbatore', 11.0168, 76.9558],
    ['Visakhapatnam', 17.6868, 83.2185], ['Guwahati', 26.1445, 91.7362], ['Chandigarh', 30.7333, 76.7794],
    ['Varanasi', 25.3176, 82.9739], ['Patna', 25.5941, 85.1376], ['Bhubaneswar', 20.2961, 85.8245],
    ['Madurai', 9.9252, 78.1198], ['Raipur', 21.2514, 81.6296], ['Amritsar', 31.634, 74.8723],
    ['Jodhpur', 26.2389, 73.0243], ['Dehradun', 30.3165, 78.0322],
  ],
  LK: [
    ['Colombo', 6.9271, 79.8612], ['Kandy', 7.2906, 80.6337], ['Galle', 6.0535, 80.221],
    ['Jaffna', 9.6615, 80.0255], ['Anuradhapura', 8.3114, 80.4037], ['Batticaloa', 7.731, 81.6747],
  ],
  BD: [
    ['Dacca', 23.8103, 90.4125], ['Chittagong', 22.3569, 91.7832], ['Sylhet', 24.8949, 91.8687],
    ['Khulna', 22.8456, 89.5403], ['Rajshahi', 24.3745, 88.6042],
  ],
  BT: [['Thimphou', 27.4728, 89.639], ['Paro', 27.4305, 89.4133]],
  NP: [['Katmandou', 27.7172, 85.324], ['Pokhara', 28.2096, 83.9856]],
  MN: [
    ['Oulan-Bator', 47.8864, 106.9057], ['Erdenet', 49.0333, 104.0833], ['Darkhan', 49.4867, 105.9228],
    ['Khovd', 48.0056, 91.6419],
  ],
  KZ: [
    ['Almaty', 43.222, 76.8512], ['Astana', 51.1694, 71.4491], ['Chymkent', 42.3417, 69.5901],
    ['Karaganda', 49.8047, 73.1094], ['Aktobe', 50.2839, 57.167], ['Pavlodar', 52.2873, 76.9674],
    ['Öskemen', 49.9483, 82.6275], ['Kostanaï', 53.2198, 63.6354], ['Atyraou', 47.0945, 51.9238],
  ],
  KG: [['Bichkek', 42.8746, 74.5698], ['Och', 40.5283, 72.7985], ['Karakol', 42.4907, 78.3936], ['Naryn', 41.4287, 75.9911]],
  IL: [
    ['Tel Aviv', 32.0853, 34.7818], ['Jérusalem', 31.7683, 35.2137], ['Haïfa', 32.794, 34.9896],
    ['Beer-Sheva', 31.2518, 34.7913], ['Eilat', 29.5577, 34.9519], ['Tibériade', 32.7959, 35.531],
  ],
  JO: [['Amman', 31.9454, 35.9284], ['Irbid', 32.5556, 35.85], ['Aqaba', 29.5321, 35.0063], ['Wadi Moussa', 30.3216, 35.4801]],
  AE: [['Dubaï', 25.2048, 55.2708], ['Abou Dabi', 24.4539, 54.3773], ['Al-Aïn', 24.2075, 55.7447]],
  QA: [['Doha', 25.2854, 51.531]],
  LB: [['Beyrouth', 33.8938, 35.5018], ['Tripoli (Liban)', 34.4367, 35.8497]],
  AU: [
    ['Sydney', -33.8688, 151.2093], ['Melbourne', -37.8136, 144.9631], ['Brisbane', -27.4698, 153.0251],
    ['Perth', -31.9505, 115.8605], ['Adélaïde', -34.9285, 138.6007], ['Hobart', -42.8821, 147.3272],
    ['Darwin', -12.4634, 130.8456], ['Canberra', -35.2809, 149.13], ['Cairns', -16.9186, 145.7781],
    ['Townsville', -19.259, 146.8169], ['Alice Springs', -23.698, 133.8807], ['Kalgoorlie', -30.7489, 121.4658],
    ['Broome', -17.9614, 122.2359], ['Toowoomba', -27.5598, 151.9507], ['Dubbo', -32.2569, 148.6011],
    ['Mildura', -34.2068, 142.1367], ['Port Augusta', -32.4936, 137.7653], ['Albany', -35.0269, 117.8837],
    ['Geraldton', -28.7774, 114.615], ['Mount Isa', -20.7256, 139.4927], ['Rockhampton', -23.3791, 150.51],
    ['Wagga Wagga', -35.1082, 147.3598], ['Launceston', -41.4332, 147.1441], ['Bendigo', -36.757, 144.2794],
    ['Esperance', -33.8613, 121.8914], ['Katherine', -14.465, 132.2635],
  ],
  NZ: [
    ['Auckland', -36.8485, 174.7633], ['Wellington', -41.2865, 174.7762], ['Christchurch', -43.5321, 172.6362],
    ['Queenstown', -45.0312, 168.6626], ['Dunedin', -45.8788, 170.5028], ['Hamilton', -37.787, 175.2793],
    ['Napier', -39.4928, 176.912], ['Nelson', -41.2706, 173.284], ['Rotorua', -38.1368, 176.2497],
    ['Invercargill', -46.4132, 168.3538], ['Greymouth', -42.4499, 171.2077], ['New Plymouth', -39.0556, 174.0752],
    ['Whangārei', -35.7251, 174.3237], ['Gisborne', -38.6623, 178.0176],
  ],
  US: [
    ['New York', 40.7128, -74.006], ['Los Angeles', 34.0522, -118.2437], ['Chicago', 41.8781, -87.6298],
    ['Houston', 29.7604, -95.3698], ['Phoenix', 33.4484, -112.074], ['Philadelphie', 39.9526, -75.1652],
    ['San Antonio', 29.4241, -98.4936], ['San Diego', 32.7157, -117.1611], ['Dallas', 32.7767, -96.797],
    ['San Francisco', 37.7749, -122.4194], ['Seattle', 47.6062, -122.3321], ['Denver', 39.7392, -104.9903],
    ['Boston', 42.3601, -71.0589], ['Atlanta', 33.749, -84.388], ['Miami', 25.7617, -80.1918],
    ['Minneapolis', 44.9778, -93.265], ['Détroit', 42.3314, -83.0458], ['Portland (Oregon)', 45.5152, -122.6784],
    ['Las Vegas', 36.1699, -115.1398], ['Salt Lake City', 40.7608, -111.891], ['Kansas City', 39.0997, -94.5786],
    ['Nashville', 36.1627, -86.7816], ['La Nouvelle-Orléans', 29.9511, -90.0715], ['Saint-Louis', 38.627, -90.1994],
    ['Albuquerque', 35.0844, -106.6504], ['Boise', 43.615, -116.2023], ['Billings', 45.7833, -108.5007],
    ['Bismarck', 46.8083, -100.7837], ['Omaha', 41.2565, -95.9345], ['Oklahoma City', 35.4676, -97.5164],
    ['Little Rock', 34.7465, -92.2896], ['Memphis', 35.1495, -90.049], ['Jackson', 32.2988, -90.1848],
    ['Birmingham (Alabama)', 33.5186, -86.8104], ['Charlotte', 35.2271, -80.8431], ['Raleigh', 35.7796, -78.6382],
    ['Richmond', 37.5407, -77.436], ['Washington', 38.9072, -77.0369], ['Pittsburgh', 40.4406, -79.9959],
    ['Cleveland', 41.4993, -81.6944], ['Columbus', 39.9612, -82.9988], ['Indianapolis', 39.7684, -86.1581],
    ['Milwaukee', 43.0389, -87.9065], ['Des Moines', 41.5868, -93.625], ['Fargo', 46.8772, -96.7898],
    ['Sioux Falls', 43.5446, -96.7311], ['Cheyenne', 41.14, -104.8202], ['Rapid City', 44.0805, -103.231],
    ['Spokane', 47.6588, -117.426], ['Reno', 39.5296, -119.8138], ['Sacramento', 38.5816, -121.4944],
    ['Fresno', 36.7378, -119.7871], ['Eugene', 44.0521, -123.0868], ['Tucson', 32.2226, -110.9747],
    ['El Paso', 31.7619, -106.485], ['Amarillo', 35.222, -101.8313], ['Austin', 30.2672, -97.7431],
    ['Jacksonville', 30.3322, -81.6557], ['Tampa', 27.9506, -82.4572], ['Tallahassee', 30.4383, -84.2807],
    ['Savannah', 32.0809, -81.0912], ['Knoxville', 35.9606, -83.9207], ['Louisville', 38.2527, -85.7585],
    ['Cincinnati', 39.1031, -84.512], ['Buffalo', 42.8864, -78.8784], ['Albany (New York)', 42.6526, -73.7562],
    ['Burlington', 44.4759, -73.2121], ['Portland (Maine)', 43.6591, -70.2568], ['Bangor', 44.8012, -68.7778],
    ['Duluth', 46.7867, -92.1005], ['Marquette', 46.5436, -87.3954], ['Missoula', 46.8721, -113.994],
    ['Helena', 46.5891, -112.0391], ['Flagstaff', 35.1983, -111.6513], ['Grand Junction', 39.0639, -108.5506],
    ['Wichita', 37.6872, -97.3301], ['Lubbock', 33.5779, -101.8552], ['Shreveport', 32.5252, -93.7502],
    ['Anchorage', 61.2181, -149.9003], ['Fairbanks', 64.8378, -147.7164], ['Juneau', 58.3019, -134.4197],
    ['Honolulu', 21.3069, -157.8583, 'oceania'], ['Hilo', 19.7071, -155.0816, 'oceania'],
  ],
  CA: [
    ['Toronto', 43.6532, -79.3832], ['Montréal', 45.5017, -73.5673], ['Vancouver', 49.2827, -123.1207],
    ['Calgary', 51.0447, -114.0719], ['Edmonton', 53.5461, -113.4938], ['Ottawa', 45.4215, -75.6972],
    ['Winnipeg', 49.8951, -97.1384], ['Québec', 46.8139, -71.208], ['Halifax', 44.6488, -63.5752],
    ['Saskatoon', 52.1332, -106.67], ['Regina', 50.4452, -104.6189], ['Victoria', 48.4284, -123.3656],
    ["St. John's", 47.5615, -52.7126], ['Thunder Bay', 48.3809, -89.2477], ['Sudbury', 46.4917, -80.993],
    ['Kelowna', 49.888, -119.496], ['Prince George', 53.9171, -122.7497], ['Moncton', 46.0878, -64.7782],
    ['Charlottetown', 46.2382, -63.1311], ['Saguenay', 48.4284, -71.0685], ['Rimouski', 48.4489, -68.5236],
    ['Whitehorse', 60.7212, -135.0568], ['Yellowknife', 62.454, -114.3718], ['Fort McMurray', 56.7267, -111.379],
    ['Kamloops', 50.6745, -120.3273], ['Sault-Sainte-Marie', 46.5219, -84.3461], ["Val-d'Or", 48.0975, -77.7974],
    ['Brandon', 49.8485, -99.9501], ['Lethbridge', 49.6956, -112.8451], ['Grande Prairie', 55.1707, -118.7947],
  ],
  MX: [
    ['Mexico', 19.4326, -99.1332], ['Guadalajara', 20.6597, -103.3496], ['Monterrey', 25.6866, -100.3161],
    ['Puebla', 19.0414, -98.2063], ['Tijuana', 32.5149, -117.0382], ['Mérida', 20.9674, -89.5926],
    ['Oaxaca', 17.0732, -96.7266], ['Chihuahua', 28.632, -106.0691], ['Hermosillo', 29.0729, -110.9559],
    ['Culiacán', 24.8091, -107.394], ['Veracruz', 19.1738, -96.1342], ['Acapulco', 16.8531, -99.8237],
    ['Cancún', 21.1619, -86.8515], ['San Luis Potosí', 22.1565, -100.9855], ['Durango', 24.0277, -104.6532],
    ['La Paz (Basse-Californie)', 24.1426, -110.3128], ['Tuxtla Gutiérrez', 16.7516, -93.1029],
    ['Zacatecas', 22.7709, -102.5832], ['Torreón', 25.5428, -103.4068], ['Morelia', 19.706, -101.195],
    ['Villahermosa', 17.9892, -92.9475], ['Campeche', 19.8301, -90.5349], ['Tampico', 22.2331, -97.8611],
  ],
  GT: [['Guatemala', 14.6349, -90.5069], ['Quetzaltenango', 14.8347, -91.5181], ['Cobán', 15.4706, -90.3703]],
  CR: [['San José', 9.9281, -84.0907], ['Liberia', 10.6346, -85.4377], ['Limón', 9.9907, -83.036]],
  PA: [['Panama', 8.9824, -79.5199], ['David', 8.4273, -82.4309]],
  DO: [['Saint-Domingue', 18.4861, -69.9312], ['Santiago de los Caballeros', 19.4517, -70.697], ['Punta Cana', 18.5601, -68.3725]],
  PR: [['San Juan', 18.4655, -66.1057], ['Ponce', 18.0111, -66.6141], ['Mayagüez', 18.2013, -67.1397]],
  BR: [
    ['São Paulo', -23.5505, -46.6333], ['Rio de Janeiro', -22.9068, -43.1729], ['Brasília', -15.7975, -47.8919],
    ['Salvador', -12.9777, -38.5016], ['Fortaleza', -3.7319, -38.5267], ['Belo Horizonte', -19.9167, -43.9345],
    ['Manaus', -3.119, -60.0217], ['Curitiba', -25.4284, -49.2733], ['Recife', -8.0476, -34.877],
    ['Porto Alegre', -30.0346, -51.2177], ['Belém', -1.4558, -48.4902], ['Goiânia', -16.6869, -49.2648],
    ['Campo Grande', -20.4697, -54.6201], ['Cuiabá', -15.6014, -56.0979], ['São Luís', -2.5307, -44.3068],
    ['Teresina', -5.0892, -42.8019], ['Natal', -5.7945, -35.211], ['Florianópolis', -27.5954, -48.548],
    ['Vitória', -20.3155, -40.3128], ['Porto Velho', -8.7612, -63.9004], ['Palmas', -10.2491, -48.3243],
    ['Uberlândia', -18.9186, -48.2772], ['Londrina', -23.3045, -51.1696], ['Montes Claros', -16.735, -43.8617],
    ['Feira de Santana', -12.2664, -38.9663], ['Chapecó', -27.1004, -52.6152], ['Rio Branco', -9.9754, -67.8249],
    ['Boa Vista', 2.8235, -60.6758], ['Macapá', 0.0349, -51.0694], ['Juazeiro do Norte', -7.2131, -39.3151],
    ['Petrolina', -9.3891, -40.503], ['Santarém', -2.4385, -54.6996], ['Barreiras', -12.1528, -44.99],
    ['Pelotas', -31.7654, -52.3376], ['Ribeirão Preto', -21.1704, -47.8103],
  ],
  AR: [
    ['Buenos Aires', -34.6037, -58.3816], ['Córdoba', -31.4201, -64.1888], ['Rosario', -32.9442, -60.6505],
    ['Mendoza', -32.8895, -68.8458], ['Salta', -24.7821, -65.4232], ['San Miguel de Tucumán', -26.8083, -65.2176],
    ['Mar del Plata', -38.0055, -57.5426], ['San Carlos de Bariloche', -41.1335, -71.3103], ['Neuquén', -38.9516, -68.0591],
    ['Bahía Blanca', -38.7196, -62.2724], ['Comodoro Rivadavia', -45.8641, -67.4966], ['Ushuaïa', -54.8019, -68.303],
    ['Posadas', -27.3671, -55.8961], ['Resistencia', -27.4606, -58.9839], ['Santa Rosa', -36.6167, -64.2833],
    ['San Juan', -31.5375, -68.5364], ['Río Gallegos', -51.623, -69.2168], ['Corrientes', -27.4692, -58.8306],
    ['Santiago del Estero', -27.7951, -64.2615], ['Puerto Madryn', -42.7692, -65.0385], ['El Calafate', -50.3379, -72.2648],
  ],
  CL: [
    ['Santiago', -33.4489, -70.6693], ['Valparaíso', -33.0472, -71.6127], ['Concepción', -36.8201, -73.0444],
    ['Antofagasta', -23.6509, -70.3975], ['La Serena', -29.9027, -71.2519], ['Temuco', -38.7359, -72.5904],
    ['Puerto Montt', -41.4689, -72.9411], ['Arica', -18.4783, -70.3126], ['Iquique', -20.2307, -70.1357],
    ['Punta Arenas', -53.1638, -70.9171], ['Coyhaique', -45.5712, -72.0685], ['Calama', -22.4544, -68.9294],
    ['Copiapó', -27.3668, -70.3323], ['Talca', -35.4264, -71.6554], ['Valdivia', -39.8142, -73.2459],
    ['Chillán', -36.6066, -72.1034],
  ],
  PE: [
    ['Lima', -12.0464, -77.0428], ['Arequipa', -16.409, -71.5375], ['Cuzco', -13.532, -71.9675],
    ['Trujillo', -8.1091, -79.0215], ['Chiclayo', -6.7714, -79.8409], ['Piura', -5.1945, -80.6328],
    ['Iquitos', -3.7491, -73.2538], ['Huancayo', -12.0651, -75.2049], ['Puno', -15.8402, -70.0219],
    ['Cajamarca', -7.1617, -78.5128], ['Ayacucho', -13.1588, -74.2239], ['Tacna', -18.0066, -70.2463],
    ['Huaraz', -9.5278, -77.5278], ['Pucallpa', -8.3791, -74.5539],
  ],
  CO: [
    ['Bogota', 4.711, -74.0721], ['Medellín', 6.2442, -75.5812], ['Cali', 3.4516, -76.532],
    ['Barranquilla', 10.9685, -74.7813], ['Carthagène', 10.391, -75.4794], ['Bucaramanga', 7.1193, -73.1227],
    ['Pereira', 4.8133, -75.6961], ['Santa Marta', 11.2408, -74.199], ['Cúcuta', 7.8939, -72.5078],
    ['Pasto', 1.2136, -77.2811], ['Villavicencio', 4.142, -73.6266], ['Neiva', 2.9273, -75.2819],
    ['Montería', 8.7479, -75.8814], ['Popayán', 2.4448, -76.6147], ['Tunja', 5.5353, -73.3678],
  ],
  EC: [
    ['Quito', -0.1807, -78.4678], ['Guayaquil', -2.171, -79.9224], ['Cuenca', -2.9001, -79.0059],
    ['Loja', -3.9931, -79.2042], ['Manta', -0.9677, -80.7089], ['Ambato', -1.2491, -78.6168],
    ['Esmeraldas', 0.9682, -79.6517],
  ],
  BO: [
    ['La Paz', -16.4897, -68.1193], ['Santa Cruz de la Sierra', -17.8146, -63.1561], ['Cochabamba', -17.414, -66.1653],
    ['Sucre', -19.0196, -65.262], ['Oruro', -17.9647, -67.106], ['Potosí', -19.5836, -65.7531],
    ['Tarija', -21.5355, -64.7296], ['Uyuni', -20.46, -66.825],
  ],
  UY: [
    ['Montevideo', -34.9011, -56.1645], ['Salto', -31.3833, -57.9667], ['Punta del Este', -34.9475, -54.9338],
    ['Tacuarembó', -31.711, -55.98], ['Rivera', -30.9053, -55.5508],
  ],
  PY: [
    ['Asunción', -25.2637, -57.5759], ['Ciudad del Este', -25.5097, -54.6111], ['Encarnación', -27.3306, -55.8667],
    ['Concepción', -23.4064, -57.4344],
  ],
  ZA: [
    ['Johannesburg', -26.2041, 28.0473], ['Le Cap', -33.9249, 18.4241], ['Durban', -29.8587, 31.0218],
    ['Pretoria', -25.7479, 28.2293], ['Gqeberha', -33.9608, 25.6022], ['Bloemfontein', -29.0852, 26.1596],
    ['East London', -33.0153, 27.9116], ['Polokwane', -23.9045, 29.4689], ['Mbombela', -25.4658, 30.9853],
    ['Kimberley', -28.7282, 24.7499], ['Upington', -28.4478, 21.2561], ['George', -33.963, 22.4617],
    ['Pietermaritzburg', -29.6006, 30.3794], ['Mthatha', -31.5889, 28.7844], ['Rustenburg', -25.6676, 27.2421],
    ['Springbok', -29.664, 17.8865],
  ],
  BW: [['Gaborone', -24.6282, 25.9231], ['Francistown', -21.17, 27.5072], ['Maun', -19.9833, 23.4167]],
  LS: [['Maseru', -29.3151, 27.4869]],
  SZ: [['Mbabane', -26.3054, 31.1367], ['Manzini', -26.4833, 31.3667]],
  KE: [
    ['Nairobi', -1.2921, 36.8219], ['Mombasa', -4.0435, 39.6682], ['Kisumu', -0.0917, 34.768],
    ['Nakuru', -0.3031, 36.08], ['Eldoret', 0.5143, 35.2698], ['Nyeri', -0.4201, 36.9476],
  ],
  UG: [['Kampala', 0.3476, 32.5825], ['Gulu', 2.7724, 32.2881], ['Mbarara', -0.6072, 30.6545], ['Jinja', 0.4244, 33.2042]],
  RW: [['Kigali', -1.9441, 30.0619], ['Huye', -2.5967, 29.7394]],
  NG: [
    ['Lagos', 6.5244, 3.3792], ['Abuja', 9.0765, 7.3986], ['Ibadan', 7.3775, 3.947], ['Kano', 12.0022, 8.592],
    ['Port Harcourt', 4.8156, 7.0498], ['Enugu', 6.4584, 7.5464], ['Benin City', 6.335, 5.6037],
  ],
  GH: [
    ['Accra', 5.6037, -0.187], ['Kumasi', 6.6885, -1.6244], ['Tamale', 9.4008, -0.8393],
    ['Takoradi', 4.8845, -1.7554], ['Cape Coast', 5.1053, -1.2466],
  ],
  SN: [
    ['Dakar', 14.7167, -17.4677], ['Saint-Louis', 16.0326, -16.4818], ['Thiès', 14.791, -16.9359],
    ['Ziguinchor', 12.5641, -16.264], ['Tambacounda', 13.7707, -13.6673],
  ],
  TN: [
    ['Tunis', 36.8065, 10.1815], ['Sfax', 34.7406, 10.7603], ['Sousse', 35.8256, 10.6084],
    ['Gabès', 33.8815, 10.0982], ['Bizerte', 37.2744, 9.8739], ['Tozeur', 33.9197, 8.1335],
  ],
  MG: [['Antananarivo', -18.8792, 47.5079], ['Toamasina', -18.1443, 49.3958], ['Antsirabe', -19.8659, 47.0333]],
  MA: [
    ['Casablanca', 33.5731, -7.5898], ['Marrakech', 31.6295, -7.9811], ['Fès', 34.0181, -5.0078],
    ['Rabat', 34.0209, -6.8416], ['Tanger', 35.7595, -5.834], ['Agadir', 30.4278, -9.5981],
  ],
  EG: [['Le Caire', 30.0444, 31.2357], ['Alexandrie', 31.2001, 29.9187], ['Louxor', 25.6872, 32.6396]],
  TZ: [['Dar es Salaam', -6.7924, 39.2083], ['Arusha', -3.3869, 36.683], ['Zanzibar', -6.1659, 39.2026]],
  NA: [['Windhoek', -22.5609, 17.0658], ['Swakopmund', -22.6784, 14.5266]],
};

export const PLACES: Place[] = Object.entries(PLACE_TUPLES).flatMap(([country, tuples]) =>
  tuples.map(([name, lat, lng, continent]) => ({
    name,
    country,
    lat,
    lng,
    continent: continent ?? COUNTRY_BY_CODE[country]?.continent ?? 'europe',
  })),
);
